import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUserCharactersApi, Character, uploadFile as uploadFileApi, apiPost, deleteCharacterApi } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import LoadingSpinner from '../components/LoadingSpinner.tsx';

// --- helpers ----------------------------------------------------

const hasGenerating = (characters: Character[] | undefined) =>
  !!characters?.some(c => !c.image_url || c.status === 'generating');


// --- page -------------------------------------------------------

const Characters: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  
  // Character creation modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [characterName, setCharacterName] = useState('');
  const [characterAge, setCharacterAge] = useState(8);
  const [artStyle, setArtStyle] = useState('storybook watercolor, soft and gentle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [aiClothing, setAiClothing] = useState(true);
  const [clothingDescription, setClothingDescription] = useState('');

  // Character view modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedCharacter, setSelectedCharacter] = useState<Character | null>(null);
  
  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState<Character | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const {
    data: characters = [],
    isLoading,
    isFetching,
    error,
  } = useQuery<Character[]>({
    queryKey: ['user-characters'],
    queryFn: () => getUserCharactersApi(false),
    refetchInterval: (query) => (hasGenerating(query.state.data as Character[]) ? 2000 : false),
    refetchOnWindowFocus: false,
    placeholderData: (previousData) => previousData,
    staleTime: 60_000, // cache for 1 minute
    enabled: !authLoading && isAuthenticated,
  });

  // Art style options (same as StorySetup)
  const artStyleOptions = [
    { value: 'storybook watercolor, soft and gentle', label: 'Storybook Watercolor' },
    { value: 'cartoon with smooth vector shading, clean inked outlines, soft glossy finish', label: 'Cartoon Style' },
    { value: 'illustrated with soft watercolor texture, clean pencil outlines, matte paper finish', label: 'Illustrated' },
    { value: 'realistic with natural lighting, detailed textures, photorealistic rendering', label: 'Realistic' }
  ];

  // Character creation mutation
  const createCharacterMutation = useMutation({
    mutationFn: async (characterData: { name: string; age: number; artStyle: string; imageBase64: string; filename: string; clothingDescription?: string; aiClothing?: boolean }) => {
      const response = await apiPost('/characters/create-from-image', {
        uploaded_characters: [{
          name: characterData.name,
          age: characterData.age,
          art_style: characterData.artStyle,
          image_base64: characterData.imageBase64,
          filename: characterData.filename,
          clothing_description: characterData.clothingDescription,
          ai_clothing: characterData.aiClothing ?? true,
        }]
      });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-characters'] });
      setCharacterName('');
      setCharacterAge(8);
      setArtStyle('storybook watercolor, soft and gentle');
      setSelectedFile(null);
      setAiClothing(true);
      setClothingDescription('');
    },
  });

  // Character deletion mutation
  const deleteCharacterMutation = useMutation({
    mutationFn: async (characterId: number) => {
      await deleteCharacterApi(characterId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-characters'] });
    },
  });

  // Handle file upload and character creation
  const handleFileUpload = async (file: File) => {
    if (!file) return;
    
    // Validate character name (single word only)
    const trimmedName = characterName.trim();
    if (!trimmedName || trimmedName.includes(' ') || trimmedName.includes('\t') || trimmedName.includes('\n')) {
      alert('Character name must be a single word (no spaces).');
      return;
    }
    
    setIsUploading(true);
    try {
      // Close modal immediately and optimistically add a generating card
      setShowCreateModal(false);
      const tempId = Date.now();
      const prev = queryClient.getQueryData<Character[]>(['user-characters']) || [];
      const tempCard: Character = {
        id: tempId as unknown as number,
        name: characterName.trim(),
        description: 'Generating...'.toString(),
        image_url: '',
        status: 'generating' as any,
        character_type: 'main',
        source_type: 'upload',
        character_age: characterAge,
        created_at: new Date().toISOString() as any,
      } as unknown as Character;
      queryClient.setQueryData<Character[]>(['user-characters'], [tempCard, ...prev]);

      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data:image/jpeg;base64, prefix
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      
      // Create character with base64 image
      await createCharacterMutation.mutateAsync({
        name: trimmedName,
        age: characterAge,
        artStyle: artStyle,
        imageBase64: base64,
        filename: file.name,
        clothingDescription,
        aiClothing,
      });
      // After success, refetch to swap the temp card with real record
      await queryClient.invalidateQueries({ queryKey: ['user-characters'] });
    } catch (error) {
      console.error('Error creating character:', error);
      alert('Failed to create character. Please try again.');
      // Remove temp card on failure
      queryClient.setQueryData<Character[]>(['user-characters'], (old) =>
        (old || []).filter((c) => (c as any).id !== tempId)
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleCharacterClick = (character: Character) => {
    setSelectedCharacter(character);
    setShowViewModal(true);
  };

  const handleDeleteClick = (character: Character) => {
    setCharacterToDelete(character);
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!characterToDelete) return;
    
    try {
      await deleteCharacterMutation.mutateAsync(characterToDelete.id);
      // Close both modals
      setShowViewModal(false);
      setShowDeleteModal(false);
      setSelectedCharacter(null);
      setCharacterToDelete(null);
      setDeleteError(null);
    } catch (error) {
      console.error('Error deleting character:', error);
      
      // Check if it's a 404 error (character not found or linked to story)
      if (error instanceof Error && (
        error.message.includes('404') || 
        error.message.includes('Character not found') ||
        error.message.includes('not owned by user')
      )) {
        setDeleteError(`Cannot delete "${characterToDelete.name}" because it is linked to a story. Only independent characters can be deleted.`);
      } else {
        setDeleteError('Failed to delete character. Please try again.');
      }
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setCharacterToDelete(null);
    setDeleteError(null);
  };

  // auth gates
  if (authLoading) {
    return (
      <LoadingSpinner
        size="large"
        message="Checking your session..."
        className="bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100"
      />
    );
  }
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-6xl mb-6">🔒</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Please log in</h2>
          <p className="text-gray-600">You need to sign in to view your characters.</p>
        </div>
      </div>
    );
  }

  // initial load
  if (isLoading && characters.length === 0) {
    return (
      <LoadingSpinner
        size="large"
        message="Loading characters..."
        className="bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100"
      />
    );
  }

  // error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Characters</h2>
          <p className="text-gray-600 mb-4">
            {(error as Error).message || 'Failed to fetch characters'}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // empty
  if (characters.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="text-6xl mb-6">👥</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">No Characters Found Yet</h2>
          <p className="text-gray-600 mb-6">
            Create characters from images or generate them through stories. Your character library will appear here.
          </p>
          <button
            onClick={() => (window.location.href = '/story-setup')}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all duration-200 hover:scale-105 font-medium"
          >
            Create Your First Story
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-50 to-blue-100">
      <div className="container mx-auto px-6 py-8">
        {/* Back */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/home')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Home</span>
          </button>
        </div>

        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h1 className="text-4xl font-bold text-gray-800">Your Characters</h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full p-3 hover:from-purple-700 hover:to-pink-700 transition-all duration-200 hover:scale-105 shadow-lg"
              title="Create New Character"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
          <p className="text-gray-600">
            Your character library
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {characters.map((character) => (
            <CharacterCard 
              key={character.id} 
              character={character} 
              onClick={() => handleCharacterClick(character)}
            />
          ))}
        </div>

        {/* Character Creation Modal */}
        {showCreateModal && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowCreateModal(false);
              }
            }}
          >
            <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Create New Character</h2>
              
              <div className="space-y-4">
                {/* Character Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Character Name
                  </label>
                  <input
                    type="text"
                    value={characterName}
                    onChange={(e) => setCharacterName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="e.g., Alex, Luna, Max (single word only)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use a single word name (no spaces) for better story generation
                  </p>
                </div>

                {/* Character Age */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Character Age
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={characterAge}
                    onChange={(e) => setCharacterAge(parseInt(e.target.value) || 8)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter character age (1-100)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Age of the character in the story (1-100 years)
                  </p>
                </div>

                {/* Art Style */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Art Style
                  </label>
                  <select
                    value={artStyle}
                    onChange={(e) => setArtStyle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    {artStyleOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Choose the visual style for your character
                  </p>
                </div>

                {/* Clothing Options */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Clothing Description
                    </label>
                    <label className="flex items-center text-sm text-gray-600 gap-2">
                      <input
                        type="checkbox"
                        checked={aiClothing}
                        onChange={(e) => setAiClothing(e.target.checked)}
                        className="h-4 w-4 text-purple-600 border-gray-300 rounded"
                      />
                      AI Generated (recommended)
                    </label>
                  </div>
                  <textarea
                    value={clothingDescription}
                    onChange={(e) => setClothingDescription(e.target.value)}
                    disabled={aiClothing}
                    placeholder="Describe clothing (e.g., blue hoodie, black jeans, white sneakers)."
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent min-h-[80px] ${aiClothing ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    If AI Generated is checked, the system will choose simple clothing inspired by the reference outfit’s color palette.
                  </p>
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Character Image
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {selectedFile ? (
                      <div>
                        <p className="text-sm text-gray-600 mb-2">Selected: {selectedFile.name}</p>
                        <button
                          onClick={() => setSelectedFile(null)}
                          className="text-red-500 text-sm hover:text-red-700"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                          className="hidden"
                          id="character-image"
                        />
                        <label
                          htmlFor="character-image"
                          className="cursor-pointer text-purple-600 hover:text-purple-700"
                        >
                          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          <p className="text-sm">Click to upload image</p>
                        </label>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div className="flex gap-3 mt-8">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => selectedFile && handleFileUpload(selectedFile)}
                  disabled={!characterName.trim() || !selectedFile || isUploading}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Creating...
                    </div>
                  ) : (
                    'Create Character'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Character View Modal */}
        {showViewModal && selectedCharacter && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowViewModal(false);
                setSelectedCharacter(null);
              }
            }}
          >
            <div className="bg-white rounded-xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedCharacter.name}</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleDeleteClick(selectedCharacter)}
                      disabled={deleteCharacterMutation.isPending}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 hover:bg-red-200 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleteCharacterMutation.isPending ? 'Deleting...' : 'Delete'}
                    </button>
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        setSelectedCharacter(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 text-2xl"
                    >
                      ×
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Character Image */}
                  <div className="space-y-4">
                    <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 min-h-[400px]">
                      {selectedCharacter.image_url ? (
                        <img
                          src={selectedCharacter.image_url}
                          alt={selectedCharacter.name}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Character Details */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Character Details</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Name:</span>
                          <span className="font-medium">{selectedCharacter.name}</span>
                        </div>
                        {selectedCharacter.character_age && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age:</span>
                            <span className="font-medium">{selectedCharacter.character_age} years old</span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type:</span>
                          <span className="font-medium capitalize">{selectedCharacter.character_type || 'Main'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Source:</span>
                          <span className="font-medium capitalize">{selectedCharacter.source_type || 'AI'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Status:</span>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            selectedCharacter.status === 'completed' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {selectedCharacter.status === 'completed' ? 'Ready' : 'Generating'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {selectedCharacter.description && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">
                          {selectedCharacter.description}
                        </p>
                      </div>
                    )}
                    
                    {selectedCharacter.created_at && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Created</h3>
                        <p className="text-gray-600 text-sm">
                          {new Date(selectedCharacter.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    )}
                    
                    {/* Create Story Button */}
                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={() => {
                          // Close modal and navigate to story setup with character pre-selected
                          setShowViewModal(false);
                          setSelectedCharacter(null);
                          navigate(`/story-setup?character=${selectedCharacter.id}`);
                        }}
                        className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
                      >
                        Create Story with {selectedCharacter.name}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && characterToDelete && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleDeleteCancel();
              }
            }}
          >
            <div className="bg-white rounded-xl max-w-md w-full p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Delete Character</h3>
                
                {deleteError ? (
                  <div className="mb-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-red-800">{deleteError}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 mb-6">
                    Are you sure you want to delete <span className="font-medium">"{characterToDelete.name}"</span>? 
                    This action cannot be undone.
                  </p>
                )}
                
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleDeleteCancel}
                    disabled={deleteCharacterMutation.isPending}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50"
                  >
                    {deleteError ? 'Close' : 'Cancel'}
                  </button>
                  {!deleteError && (
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={deleteCharacterMutation.isPending}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      {deleteCharacterMutation.isPending ? 'Deleting...' : 'Delete'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};



// --- card (memoized) ---------------------------------------------

interface CharacterCardProps {
  character: Character;
  onClick: () => void;
}

const CharacterCard: React.FC<CharacterCardProps> = ({ character, onClick }) => {
  const [imageError, setImageError] = useState(false);
  const isGenerating = !character.image_url || character.status === 'generating';

  return (
    <div 
      className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-4 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105 border border-gray-200 cursor-pointer"
      onClick={onClick}
    >
      <div className="aspect-square mb-4 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center relative">
        {character.image_url && !imageError ? (
          <img
            src={character.image_url}
            alt={character.name}
            className="w-full h-full object-contain"
            loading="lazy"
            decoding="async"
            sizes="(min-width:1280px) 20vw, (min-width:1024px) 25vw, (min-width:768px) 33vw, 50vw"
            onError={() => setImageError(true)}
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-gray-400">
            {isGenerating ? (
              <>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mb-2"></div>
                <div className="text-sm">Generating...</div>
              </>
            ) : (
              <div className="text-4xl">👤</div>
            )}
          </div>
        )}
      </div>

      <div className="text-center">
        <h3 className="font-semibold text-gray-800 mb-1 truncate" title={character.name}>
          {character.name}
        </h3>
        <p className="text-sm text-gray-500 mb-2 line-clamp-2" title={character.description}>
          {character.description}
        </p>
        <div className="flex items-center justify-center space-x-2">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              isGenerating ? 'bg-yellow-100 text-yellow-800 animate-pulse' : 
              character.status === 'pending' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
            }`}
          >
            {isGenerating ? 'Generating' : 
             character.status === 'pending' ? 'Ready' : 'Complete'}
          </span>
          {character.character_type && character.character_type !== 'main' && (
            <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {character.character_type}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};


export default Characters;
